import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Product, ProductStockInfo } from "@api/throughShipment/onlineOrderApi";
import PopupBackground from "@component/PopupBackground";
import { Button, Form, Row, Spin } from "antd";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  onlineOrderState,
  fetchCreateProduct,
  fetchUpdateProduct,
  fetchProductDetailBySku,
  fetchProductStockBySku,
} from "@redux/onlineOrderSlice";
import { useParams } from "react-router-dom";
import { storageTypeOptions, TcatStorageKey } from "@constant/StorageType";
import { WarehouseCode } from "@constant/WarehouseCodeValue";
import CommonFormFields from "./components/CommonFormFields";
import TcatWarehouseFields from "./components/TcatWarehouseFields";
import RentrapWarehouseFields from "./components/RentrapWarehouseFields";

const Wrapper = styled(Form)`
  position: relative;
  min-width: 650px;
  padding: 30px;
  background: ${({ theme }) => theme.colorNeutral100};
  overflow: auto;
  .ant-form-item-label {
    max-width: 131px;
    min-width: 131px;
    text-align: left;
  }
  .ant-form-item-required::before {
    order: 1;
  }
  .ant-form-item-required::after {
    display: none;
  }
  .ant-input {
    width: 230px;
  }
  .ant-form-item {
    margin-bottom: 5px;
  }
  .ant-select {
    width: 230px;
  }
`;

type Props = {
  onClose: () => void;
  currentProduct?: Product;
  warehouseCode: string;
  isB2B: boolean;
  warehouseIsTcat: boolean;
};

export default function EditProductPopup(props: Props) {
  const { onClose, currentProduct, warehouseCode, isB2B, warehouseIsTcat } = props;
  const dispatch = useDispatch();
  const { orderId } = useParams();
  const { productBarcodeInfo, productStockInfo, isProductInfoFetching } = useSelector(onlineOrderState);

  const [form] = Form.useForm();
  const storageType = Form.useWatch("storageType", form);
  const effectiveDateFormValue = Form.useWatch("effectiveDate", form);
  const skuFormValue: string = Form.useWatch("sku", form);
  const storageFormValue = Form.useWatch("storageType", form);

  const [error, setError] = useState<string>("");
  const [allStockList, setAllStockList] = useState<ProductStockInfo[]>([]);
  const [stockInfoList, setStockInfoList] = useState<ProductStockInfo[]>([]);
  const [stocksForEffectiveDate, setStocksForEffectiveDate] = useState<ProductStockInfo[]>([]);
  const [sameStorageTypeStocks, setSameStorageTypeStocks] = useState<ProductStockInfo[]>([]);
  const [stocksForBatch, setStocksForBatch] = useState<ProductStockInfo[]>([]);
  const [availableQty, setAvailableQty] = useState<number>(0);

  const matchingStorage = storageTypeOptions.filter((option) =>
    allStockList.some((storage) => option.key === storage.storageType),
  );

  const handleSubmit = (formValue: any) => {
    if (currentProduct) {
      // 編輯
      const params = {
        orderId,
        productId: currentProduct.id,
        sku: formValue.sku,
        storageType: warehouseCode === WarehouseCode.REN_TRAP ? formValue.storageType : TcatStorageKey.GOOD,
        estimatedQty: Number(formValue.estimatedQty),
        unitPrice: Number(formValue.unitPrice),
        batch: formValue.batch || null,
        effectiveDate: formValue.effectiveDate ? moment(formValue.effectiveDate).format("YYYY-MM-DD") : "",
      };

      dispatch(fetchUpdateProduct(params));
    } else {
      // 新增
      const params = {
        orderId,
        sku: formValue.sku,
        storageType: warehouseCode === WarehouseCode.REN_TRAP ? formValue.storageType : TcatStorageKey.GOOD,
        estimatedQty: Number(formValue.estimatedQty),
        unitPrice: formValue.unitPrice ? Number(formValue.unitPrice) : null,
        batch: formValue.batch || null,
        effectiveDate: formValue.effectiveDate ? moment(formValue.effectiveDate).format("YYYY-MM-DD") : "",
      };
      dispatch(fetchCreateProduct(params));
    }
    onClose();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (skuFormValue && warehouseCode) {
        dispatch(fetchProductDetailBySku({ sku: skuFormValue, warehouse: warehouseCode }));
        dispatch(fetchProductStockBySku({ sku: skuFormValue, warehouse: warehouseCode }));
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [dispatch, skuFormValue, warehouseCode]);

  useEffect(() => {
    const productInfo = productBarcodeInfo?.results[0];
    if (productInfo) {
      setError("");
      form.setFieldsValue({
        standardPrice: 0,
        barcode: productInfo.barcode,
        productName: productInfo.productName,
        weightedCost: productInfo.weightedCost,
      });
    } else {
      setError("查無對應商品，請確認商品品號是否存在");
      form.resetFields();
    }
  }, [productBarcodeInfo, form]);

  useEffect(() => {
    if (productStockInfo.length === 0) return;
    const stockInfo = productStockInfo[0]?.stockInfo;
    setStockInfoList(stockInfo);
    const stockMap = new Map();
    const stocks = stockInfo.filter((item) => {
      if (!stockMap.has(item.storageType)) {
        stockMap.set(item.storageType, true);
        return true;
      }
      return false;
    });
    setAllStockList(stocks);

    if (warehouseIsTcat) {
      const currentStock = stockInfo[0]?.canSalesQty || 0;
      setAvailableQty(currentStock);
      form.setFieldValue("stock", currentStock);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productStockInfo, warehouseIsTcat]);

  useEffect(() => {
    if (storageFormValue) {
      const filteredStock = stockInfoList.filter((stockInfo) => {
        return stockInfo.storageType === storageFormValue;
      });
      setSameStorageTypeStocks(filteredStock);
    }
  }, [stockInfoList, storageFormValue]);

  useEffect(() => {
    const stockMap = new Map();
    const stocks = sameStorageTypeStocks.filter((item) => {
      if (!stockMap.has(item.effectiveDate)) {
        stockMap.set(item.effectiveDate, true);
        return true;
      }
      return false;
    });
    setStocksForEffectiveDate(stocks);
  }, [sameStorageTypeStocks]);

  useEffect(() => {
    if (effectiveDateFormValue) {
      const stockMap = new Map();
      const stocks = sameStorageTypeStocks.filter((item) => {
        if (!stockMap.has(item.batch)) {
          stockMap.set(item.batch, true);
          return true;
        }
        return false;
      });
      setStocksForBatch(stocks);
    }
  }, [effectiveDateFormValue, sameStorageTypeStocks]);
  const handleBatchChange = (batch: string) => {
    const filteredStockInfo = stocksForBatch.filter((item) => {
      return item.batch === batch;
    });
    form.setFieldsValue({
      stock: filteredStockInfo[0].canSalesQty,
    });
  };

  useEffect(() => {
    if (currentProduct) {
      form.setFieldsValue({
        ...currentProduct,
        effectiveDate: currentProduct.effectiveDate ? moment(currentProduct.effectiveDate) : null,
      });
    } else {
      form.resetFields();
    }
  }, [currentProduct, form]);

  return (
    <PopupBackground close={onClose} fixed>
      <Wrapper
        form={form}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 8 }}
        labelAlign="left"
        layout="horizontal"
        colon={false}
        onFinish={handleSubmit}
        initialValues={{ standardPrice: 0 }}
      >
        <Spin spinning={isProductInfoFetching}>
          <CommonFormFields error={error} />
          {warehouseIsTcat ? (
            <TcatWarehouseFields currentProduct={currentProduct} availableQty={availableQty} isB2B={isB2B} />
          ) : (
            <RentrapWarehouseFields
              form={form}
              storageType={storageType}
              effectiveDateFormValue={effectiveDateFormValue}
              matchingStorage={matchingStorage}
              stocksForEffectiveDate={stocksForEffectiveDate}
              stocksForBatch={stocksForBatch}
              handleBatchChange={handleBatchChange}
              isB2B={isB2B}
            />
          )}
          <Row justify="end" align="middle">
            <Button type="default" style={{ marginRight: 10 }} onClick={onClose}>
              取消
            </Button>
            <Button type="primary" htmlType="submit">
              確定
            </Button>
          </Row>
        </Spin>
      </Wrapper>
    </PopupBackground>
  );
}
